import Currency from '@/types/currency'

import type {LocaleCode} from '@/locale/locales'

import {isSupportedLocale} from './is-supported-locale'

export const getCurrencyForLocale = (locale: LocaleCode) => {
    switch (locale) {
        case 'en-gb':
            return Currency.GBP
        case 'en-us':
            return Currency.USD
        case 'da-dk':
            return Currency.DKK
        case 'sv-se':
            return Currency.SEK
        case 'no-no':
            return Currency.NOK
        default:
            return Currency.EUR
    }
}

export const currencySymbolMap: Record<Currency, string> = {
    [Currency.EUR]: '€',
    [Currency.GBP]: '£',
    [Currency.USD]: '$',
    [Currency.DKK]: 'kr.',
    [Currency.SEK]: 'kr',
    [Currency.NOK]: 'kr',
}

const getCurrencyForUnknownLocale = (locale: string) => {
    if (isSupportedLocale(locale)) {
        return getCurrencyForLocale(locale)
    }

    return Currency.EUR
}

/**
 * Returns a function which, when invoked, returns the amount in a localized currency format
 * @param locale - The locale to use for localized currency formatting
 * @param currency - The currency we want to display
 * @returns {(amount: number) => string} - A function accepting the number amount to format
 */
const currencyFormatter =
    (locale: string, currency?: string, options?: Intl.NumberFormatOptions, skipFormat?: boolean) =>
    (amount: number) => {
        const numberFormat = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency || getCurrencyForUnknownLocale(locale),
            currencyDisplay: 'symbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: false,
            ...options,
        })

        const format = ({type, value}: Intl.NumberFormatPart, skipFormat?: boolean) => {
            if (type !== 'integer') {
                // The `replace` here standardizes whitespace characters
                return value.replace(/\s/g, ' ')
            }

            if (skipFormat) {
                return value
            }

            const numValue = Number(value)

            if (amount >= 1000000000) {
                const roundedValue = Math.round(numValue / 100000000) * 100000000
                const billions = roundedValue / 1000000000

                return `${billions}B`
            }

            if (amount >= 1000000) {
                const roundedValue = Math.round(numValue / 100000) * 100000
                const millions = roundedValue / 1000000

                return `${millions}M`
            }

            if (numValue >= 10000) {
                const roundedValue = Math.round(numValue / 100) * 100
                const thousands = roundedValue / 1000

                return `${thousands}k`
            }

            return value
        }

        return numberFormat
            .formatToParts(amount)
            .map((part) => format(part, skipFormat))
            .join('')
    }

export default currencyFormatter
